import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { expensesApi, type ExtractedExpense } from '../../api/expenses.api';
import { fxApi } from '../../api/fx.api';
import type { Expense, Trip } from '../../types';
import { Button } from '../ui/Button';
import { toast } from '../ui/Toast';
import { Icon, RefreshCw } from '../ui/Icon';
import { useAuthStore } from '../../stores/auth.store';
import { SplitPicker } from './SplitPicker';
import { useSplitMembers } from '../../hooks/useSplitMembers';
import ScannedExpenseRow from './ScannedExpenseRow';
import { rowIsValid, type ScannedRow } from './scannedExpense';
import { defaultExpenseDate } from './expenseDefaults';

const round2 = (n: number) => Math.round(n * 100) / 100;

interface Props {
  tripId: string;
  trip: Trip;
  /** What the scan returned — one entry per detected expense, none saved yet. */
  expenses: ExtractedExpense[];
  onSaved: (created: Expense[]) => void;
  onCancel: () => void;
}

/**
 * Review step for a scan that yielded several expenses (a statement screenshot).
 * Every row starts ticked — reviewing means unticking what you don't want —
 * and payer/split are decided once for the whole batch: a statement is one
 * person's card, not the group's.
 */
export default function ScannedExpensesReview({ tripId, trip, expenses, onSaved, onCancel }: Props) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const base = trip.budgetCurrency || 'EUR';
  const fallbackDate = defaultExpenseDate(trip);

  const [rows, setRows] = useState<ScannedRow[]>(() => expenses.map((e, i) => ({
    key:           `scanned-${i}`,
    checked:       true,
    description:   e.description ?? '',
    amount:        e.amount != null ? String(e.amount) : '',
    currency:      e.currency ?? base,
    date:          e.date ?? fallbackDate,
    category:      e.category ?? 'OTHER',
    paymentMethod: e.paymentMethod ?? 'CARD',
    status:        e.status ?? 'PAID',
  })));
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [payerId, setPayerId] = useState(user?.id ?? '');
  const [saving, setSaving] = useState(false);

  const members = useSplitMembers(trip);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(members.map(m => m.id)));

  // One rate per distinct foreign currency, not one per row: a statement with
  // eight dollar transactions is still a single FX lookup.
  const [rates, setRates] = useState<Record<string, number>>({});
  const [failedRates, setFailedRates] = useState<string[]>([]);

  const foreignCurrencies = useMemo(
    () => [...new Set(rows.filter(r => r.currency !== base).map(r => r.currency))],
    [rows, base],
  );
  const pendingRates = foreignCurrencies.filter(c => rates[c] == null && !failedRates.includes(c));
  const pendingKey = pendingRates.join(',');

  useEffect(() => {
    if (pendingKey === '') return;
    let cancelled = false;
    void (async () => {
      const results = await Promise.all(pendingKey.split(',').map(async currency => {
        try {
          return { currency, rate: (await fxApi.rate(currency, base)).rate };
        } catch {
          return { currency, rate: null };
        }
      }));
      if (cancelled) return;
      const ok = results.filter((r): r is { currency: string; rate: number } => r.rate != null);
      const bad = results.filter(r => r.rate == null).map(r => r.currency);
      if (ok.length > 0) setRates(prev => ({ ...prev, ...Object.fromEntries(ok.map(r => [r.currency, r.rate])) }));
      if (bad.length > 0) setFailedRates(prev => [...new Set([...prev, ...bad])]);
    })();
    return () => { cancelled = true; };
  }, [pendingKey, base]);

  function convert(row: ScannedRow): number | null {
    const amount = parseFloat(row.amount);
    if (!(amount > 0)) return null;
    if (row.currency === base) return round2(amount);
    const rate = rates[row.currency];
    return rate != null ? round2(amount * rate) : null;
  }

  function patchRow(key: string, patch: Partial<ScannedRow>) {
    setRows(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)));
  }

  function toggleMember(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) { if (next.size > 1) next.delete(id); }
      else next.add(id);
      return next;
    });
  }

  const checkedRows = rows.filter(r => r.checked);
  const blockedRow = checkedRows.find(r => !rowIsValid(r) || convert(r) == null);
  const total = checkedRows.reduce((sum, r) => sum + (convert(r) ?? 0), 0);
  const perPerson = selected.size > 0 && total > 0 ? (total / selected.size).toFixed(2) : null;

  async function handleSave() {
    if (checkedRows.length === 0) return;
    if (blockedRow) {
      setExpandedKey(blockedRow.key);
      toast(t('expenses.scan.fixRow'), 'error');
      return;
    }
    setSaving(true);
    const created: Expense[] = [];
    let failed = 0;
    for (const row of checkedRows) {
      const amount = round2(convert(row) ?? 0);
      const participants = [...selected];
      const each = parseFloat((amount / participants.length).toFixed(2));
      const remainder = parseFloat((amount - each * participants.length).toFixed(2));
      const needsConversion = row.currency !== base;
      const payload = {
        description:      row.description.trim(),
        category:         row.category,
        payerId,
        amount,
        currency:         base,
        originalAmount:   needsConversion ? parseFloat(row.amount) : null,
        originalCurrency: needsConversion ? row.currency : null,
        exchangeRate:     needsConversion ? rates[row.currency] : null,
        paymentMethod:    row.paymentMethod,
        status:           row.status,
        date:             row.date,
        source:           'AI',
        splits: participants.map((userId, i) => ({ userId, amount: i === 0 ? each + remainder : each })),
      } as Partial<Expense> & { splits: { userId: string; amount: number }[] };
      try {
        created.push(await expensesApi.create(tripId, payload));
      } catch {
        failed++;
      }
    }
    setSaving(false);
    if (created.length > 0) toast(t('expenses.scan.savedCount', { count: created.length }), 'success');
    if (failed > 0) toast(t('expenses.scan.savedFailed', { count: failed }), 'error');
    onSaved(created);
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {t('expenses.scan.reviewHint', { count: rows.length })}
      </p>

      <div className="space-y-1.5">
        {rows.map(row => (
          <ScannedExpenseRow
            key={row.key}
            row={row}
            expanded={expandedKey === row.key}
            onToggleChecked={() => patchRow(row.key, { checked: !row.checked })}
            onToggleExpanded={() => setExpandedKey(prev => (prev === row.key ? null : row.key))}
            onChange={patch => patchRow(row.key, patch)}
            defaultMonth={trip.startDate.slice(0, 10)}
            converted={convert(row)}
            baseCurrency={base}
            rateMissing={row.currency !== base && rates[row.currency] == null}
          />
        ))}
      </div>

      {failedRates.length > 0 && (
        <div className="flex items-center justify-between gap-2 rounded-xl border border-amber-200 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-900/10 px-3 py-2">
          <p className="text-[11px] text-amber-700 dark:text-amber-400">
            {t('expenses.scan.rateFailed', { currencies: failedRates.join(', ') })}
          </p>
          <button
            type="button"
            onClick={() => setFailedRates([])}
            disabled={pendingRates.length > 0}
            className="flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-400 hover:underline disabled:opacity-50 cursor-pointer shrink-0"
          >
            <Icon as={RefreshCw} size={12} /> {t('expenses.form.conversion.refresh')}
          </button>
        </div>
      )}

      <div>
        <label className="label" htmlFor="scanned-payer">{t('expenses.form.paidBy')}</label>
        <select id="scanned-payer" className="input" value={payerId} onChange={e => setPayerId(e.target.value)}>
          {members.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
      </div>

      <SplitPicker members={members} selected={selected} onToggle={toggleMember} perPerson={perPerson} currency={base} />

      <div className="flex items-center justify-between gap-2 pt-1 text-sm">
        <span className="text-slate-500 dark:text-slate-400">{t('expenses.scan.selectedTotal', { count: checkedRows.length })}</span>
        <span className="font-mono font-bold text-slate-900 dark:text-slate-100">{total.toFixed(2)} {base}</span>
      </div>

      <div className="flex gap-3 pt-1">
        <Button
          type="button"
          variant="cta"
          className="flex-1 disabled:opacity-50 disabled:pointer-events-none"
          loading={saving}
          disabled={checkedRows.length === 0}
          onClick={handleSave}
        >
          {t('expenses.scan.addSelected', { count: checkedRows.length })}
        </Button>
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>{t('common.cancel')}</button>
      </div>
    </div>
  );
}
